import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import PriceRowPointsSpend from '../PriceRowPointsSpend'
import Group from 'components/utils/Group'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import PriceRowPrice from '../PriceRowPrice'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import styled from 'styled-components'
import Divider from 'LuxKit/Divider'
import PriceRowSale from '../PriceRowSale'
import PriceRowPointsSale from '../PriceRowPointsSale'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import PriceRowLoyaltyPoints from '../Loyalty/PriceRowLoyaltyPoints'
import { useAppSelector } from 'hooks/reduxHooks'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import ModalContext from 'contexts/ModalContext'
import LoyaltyGenericInfoModal from 'loyalty/components/LoyaltyGenericInfoModal'
import LoyaltyBurnPriceDisplay from 'loyalty/components/LoyaltyBurnPriceDisplay'
import LoyaltySpendPointsPricingContext from 'loyalty/contexts/LoyaltySpendPointsPricingContext'
import PriceRowValueDiscount from '../Value/PriceRowValueDiscount'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'

const OrText = styled(BodyText)`
  line-height: ${rem(20)};
`

/** This height is required to align the tiles’ divider horizontally. */
const PointsContainer = styled.div`
  height: ${rem(65)};
`

interface Props {
  /**
   * The current price for the item
   * e.g. may be a sale price or regular price
   */
  price: number
  /**
   * The original/regular price for the item
   *
   * If this is different than the current price
   * the stack will show the 'sale' variant
   */
  regularPrice: number
  /**
   * Current price of the item in points
   * e.g. may be sale points or regular points
   */
  points?: number
  /**
   * The original/regular price of the item in points
   */
  pointsRegularPrice: number
  direction: 'horizontal' | 'stack'
  size: 'S' | 'L'
  variant: App.MerchandiseOfferVariant
  /**
   * Controls where the discount percentage badge is rendered.
   * - 'inline': next to the strikethrough price (default)
   * - 'above': as a standalone element above the pricing section
   * - 'disabled': not shown at all
   * @default 'inline'
   */
  discountBadgePosition?: 'inline' | 'above' | 'disabled'
}

function PriceRowMerchandiseCardPriceStack(props: Props) {
  const {
    price,
    points,
    size,
    direction,
    regularPrice,
    pointsRegularPrice,
    variant,
    discountBadgePosition = 'inline',
  } = props

  const [tier] = useCurrentLoyaltyTier()
  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const showModal = useContext(ModalContext)
  const { burnPointsLimit, isSpendPointsPage, showOffersWithinBalance } = useContext(LoyaltySpendPointsPricingContext)
  const intl = useIntl()

  const handleInfoClick = useCallback(() => {
    showModal(<LoyaltyGenericInfoModal />)
  }, [showModal])

  const isOnSale = price !== regularPrice
  const isOnPointsSale = points !== pointsRegularPrice

  const discountPercent = getValueOffPercent(regularPrice, price)
  const shouldShowDiscountPercent = isOnSale && discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const showInlineDiscount = discountBadgePosition === 'inline'
  const showAboveDiscount = discountBadgePosition === 'above'

  const hasPoints = points !== undefined
  const hasBurnLimit = burnPointsLimit !== undefined
  const shouldShowPointsPay = hasBurnLimit && hasPoints
  const shouldShowPointsOnly = isSpendPointsPage && !shouldShowPointsPay && hasPoints
  const pointsCoverPrice = shouldShowPointsPay && burnPointsLimit >= points
  const hideZeroCash = showOffersWithinBalance || pointsCoverPrice
  const showEarnPoints = isLoyaltyEnabled && !!variant.pointsEarnRatio && !shouldShowPointsOnly && !pointsCoverPrice
  if (shouldShowPointsPay) {
    return <Group direction="vertical" gap={16}>
      {showAboveDiscount && shouldShowDiscountPercent && <PriceRowValueDiscount discountPercentage={discountPercent} type="exact" />}
      <Group direction="horizontal" gap={8} verticalAlign="start">
        <LoyaltyTierIcon tier={tier} size="S" />
        <LoyaltyBurnPriceDisplay
          totalPrice={price}
          pointsToBurn={Math.min(burnPointsLimit, points)}
          priceInPoints={points}
          size={size}
          saleUnit=""
          hideZeroCash={hideZeroCash}
        />
      </Group>
      {isOnSale && <Group direction="horizontal" gap={8} verticalAlign="center">
        <PriceRowSale value={regularPrice} colour="light-foreground-alt" priceFormat="dynamicDollar" />
        {showInlineDiscount && shouldShowDiscountPercent && <PriceRowValueDiscount discountPercentage={discountPercent} type="exact" />}
      </Group>}
      {showEarnPoints && (
        <PriceRowLoyaltyPoints
          tier={tier}
          size="S"
          earnablePoints={0}
          pointsEarnRatio={variant.pointsEarnRatio}
          calculationType="ratio"
          onInfoIconClick={handleInfoClick}
        />
      )}
    </Group>
  }

  if (shouldShowPointsOnly) {
    return <Group direction="vertical" gap={16}>
      {showAboveDiscount && shouldShowDiscountPercent && <PriceRowValueDiscount discountPercentage={discountPercent} type="exact" />}
      <Group direction="horizontal" gap={8} verticalAlign="start">
        <LoyaltyTierIcon tier={tier} size="S" />
        <div>
          <PriceRowPointsSpend points={points} size={size} />
          {isOnPointsSale && <Group direction="horizontal" gap={8} verticalAlign="center">
            <PriceRowPointsSale value={pointsRegularPrice} colour="light-foreground-alt" />
            {showInlineDiscount && shouldShowDiscountPercent && <PriceRowValueDiscount discountPercentage={discountPercent} type="exact" />}
          </Group>}
        </div>
      </Group>
      {showEarnPoints && (
        <PriceRowLoyaltyPoints
          tier={tier}
          size="S"
          earnablePoints={0}
          pointsEarnRatio={variant.pointsEarnRatio}
          calculationType="ratio"
          onInfoIconClick={handleInfoClick}
        />
      )}
    </Group>
  }

  return <Group
    direction={direction === 'horizontal' ? 'horizontal' : 'vertical'}
    gap={16}
    verticalAlign={direction === 'horizontal' ? 'start' : undefined}
  >
    {showAboveDiscount && shouldShowDiscountPercent && <PriceRowValueDiscount discountPercentage={discountPercent} type="exact" />}
    <div>
      <Group direction="horizontal" gap={8} verticalAlign="start">
        <LoyaltyTierIcon tier={tier} size="S" />
        {!!points && <PriceRowPointsSpend points={points} size={size} />}
      </Group>
      {isOnPointsSale && <Group direction="horizontal" gap={8} verticalAlign="center">
        <PriceRowPointsSale value={pointsRegularPrice} colour="light-foreground-alt" />
        {showInlineDiscount && shouldShowDiscountPercent && <PriceRowValueDiscount discountPercentage={discountPercent} type="exact" />}
      </Group>}
    </div>

    <Divider kind="primary" />

    <PointsContainer>
      <Group direction="vertical" gap={4}>
        {!!price && <Group direction="horizontal" gap={8} verticalAlign="start">
          {!!points && <OrText variant="small" colour="light-foreground-alt">{intl.formatMessage({
            id: '3HTYGb6n5VJ6I02PLjXj',
            defaultMessage: 'or',
            description: 'Text separator between points price and cash price',
          })}
          </OrText>}
          <PriceRowPrice
            data-testid="merch-pdp-price-amount"
            price={price}
            size="L"
            saleUnit=""
            priceFormat="dynamicDollar"
            priceStyle="body-text"
          />
        </Group>}
        {isOnSale && <Group direction="horizontal" gap={8} verticalAlign="center">
          <PriceRowSale value={regularPrice} colour="light-foreground-alt" priceFormat="dynamicDollar" />
          {showInlineDiscount && shouldShowDiscountPercent && <PriceRowValueDiscount discountPercentage={discountPercent} type="exact" />}
        </Group>}
        {showEarnPoints && (
          <PriceRowLoyaltyPoints
            tier={tier}
            size="S"
            earnablePoints={0}
            pointsEarnRatio={variant.pointsEarnRatio}
            calculationType="ratio"
            onInfoIconClick={handleInfoClick}
          />
        )}
      </Group>
    </PointsContainer>
  </Group>
}

export default PriceRowMerchandiseCardPriceStack
